'use client';

import InputField from '@savvy-kit/patterns/input-field';
import { isNil } from 'es-toolkit';
import { useField, useFormikContext } from 'formik';

type FormikInputFieldProps = {
  readonly name: string;
  readonly id: string;
  readonly label?: string;
  readonly helperText?: string;
  readonly error?: boolean;
  readonly errorMessage?: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly type?: React.HTMLInputTypeAttribute;
  readonly autoComplete?: React.ComponentProps<'input'>['autoComplete'];
  readonly autoFocus?: boolean;
  readonly size?: 'default' | 'lg';
  readonly prependInputAddon?: React.ReactNode;
  readonly appendInputAddon?: React.ReactNode;
  readonly showInfoIcon?: boolean;
  readonly infoTooltipMessage?: string;
  readonly onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

export function FormikInputField({
  name,
  id,
  label,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  placeholder,
  type,
  autoComplete,
  autoFocus,
  size,
  prependInputAddon,
  appendInputAddon,
  showInfoIcon,
  infoTooltipMessage,
  onChange,
  onBlur,
}: FormikInputFieldProps) {
  const [field, meta] = useField(name);
  const { isSubmitting } = useFormikContext();

  const hasError = !isSubmitting && Boolean(error || (meta.touched && Boolean(meta.error)));
  const resolvedErrorMessage = hasError ? (errorMessage ?? meta.error ?? '') : undefined;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    field.onChange(e);
    if (!isNil(onChange)) {
      onChange(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    field.onBlur(e);
    if (!isNil(onBlur)) {
      onBlur(e);
    }
  };

  return (
    <InputField
      id={id}
      name={name}
      label={label}
      type={type}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      size={size}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      prependInputAddon={prependInputAddon}
      appendInputAddon={appendInputAddon}
      showInfoIcon={showInfoIcon}
      infoTooltipMessage={infoTooltipMessage}
      error={hasError}
      errorMessage={resolvedErrorMessage}
      description={hasError ? undefined : helperText}
      value={field.value ?? ''}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}
